"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

const ThreeBackground = dynamic(() => import("@/components/three-background"), {
  ssr: false,
});

function hasWebGLSupport(): boolean {
  try {
    const canvas = document.createElement("canvas");
    return Boolean(
      window.WebGLRenderingContext &&
        (canvas.getContext("webgl") || canvas.getContext("experimental-webgl")),
    );
  } catch {
    return false;
  }
}

export default function ClientThreeBackground() {
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (!hasWebGLSupport()) {
      return;
    }

    const networkInfo = (
      navigator as Navigator & { connection?: { saveData?: boolean } }
    ).connection;
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const isReducedMotion = mediaQuery.matches;
    const isSmallScreen = window.innerWidth < 1024;
    const isTouch =
      window.matchMedia("(pointer: coarse)").matches || "ontouchstart" in window;
    const saveDataEnabled = Boolean(networkInfo?.saveData);
    const cores = navigator.hardwareConcurrency ?? 4;
    const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;
    const isLowPowerDevice = cores <= 4 || memory <= 4;

    // Never render Three.js WebGL background on touch devices, small screens, reduced motion, or low power hardware
    if (isReducedMotion || isSmallScreen || isTouch || saveDataEnabled || isLowPowerDevice) {
      return;
    }

    const startRender = () => setShouldRender(true);
    const onLoad = () => {
      const requestIdleCallback =
        "requestIdleCallback" in globalThis
          ? (
              globalThis as typeof globalThis & {
                requestIdleCallback: (
                  callback: IdleRequestCallback,
                  options?: IdleRequestOptions,
                ) => number;
              }
            ).requestIdleCallback
          : undefined;

      if (requestIdleCallback) {
        requestIdleCallback(() => startRender(), { timeout: 1500 });
        return;
      }

      setTimeout(startRender, 1000);
    };

    if (document.readyState === "complete") {
      onLoad();
      return;
    }

    window.addEventListener("load", onLoad, { once: true });
    return () => window.removeEventListener("load", onLoad);
  }, []);

  if (!shouldRender) return null;
  return <ThreeBackground />;
}
